/**
 * Utilities for flattening form validation errors and mapping fields to steps.
 * Used by KYCForm for clearer validation UX on submit.
 */
import { resolveFieldConfig } from "../config/fieldConfig";
import type { FieldResolverContext } from "../config/fieldConfig/types";

/** Flattened error with path, message, and step for navigation */
export interface FlattenedError {
	path: string;
	message: string;
	step: number;
}

/**
 * Recursively flatten react-hook-form errors into { path, message, step }[].
 * Handles nested objects (primarySigner.name), array-indexed paths (ownershipTiers.0.fullName),
 * and root errors.
 */
export function flattenFormErrors(
	errors: Record<string, unknown>,
	fieldToStepMap: Record<string, number>,
	prefix = "",
): FlattenedError[] {
	const result: FlattenedError[] = [];

	for (const [key, value] of Object.entries(errors)) {
		if (key === "root" && value && typeof value === "object") {
			const rootObj = value as Record<string, unknown>;
			if ("message" in rootObj && typeof rootObj.message === "string") {
				result.push({
					path: "root",
					message: rootObj.message,
					step: 1,
				});
			} else {
				// Recurse into root if it has nested structure
				result.push(...flattenFormErrors(rootObj, fieldToStepMap, "root."));
			}
			continue;
		}

		const fullPath = prefix ? `${prefix}${key}` : key;

		if (value && typeof value === "object" && !Array.isArray(value)) {
			const obj = value as Record<string, unknown>;
			if ("message" in obj && typeof obj.message === "string") {
				// Leaf error with message — parent is first non-numeric path segment
				const segments = fullPath.split(".");
				const parentKey =
					segments.find((s) => !/^\d+$/.test(s)) ?? segments[0] ?? fullPath;
				const step = fieldToStepMap[parentKey] ?? 1;
				result.push({
					path: fullPath,
					message: obj.message,
					step,
				});
			} else {
				// Nested object or array-indexed — recurse
				result.push(...flattenFormErrors(obj, fieldToStepMap, `${fullPath}.`));
			}
		}
	}

	return result;
}

/**
 * Derive field-to-step mapping from resolved field config.
 * For nested paths like primarySigner.name, the parent field (primarySigner) maps to step.
 */
export function getFieldToStepMap(
	ctx: FieldResolverContext,
): Record<string, number> {
	const { fields } = resolveFieldConfig(ctx);
	return Object.fromEntries(fields.map((f) => [f.fieldId, f.step]));
}

/**
 * DB (snake_case) to UI (camelCase) key mapping for 422 fieldErrors.
 * BFF typically returns camelCase; this normalizes snake_case keys defensively.
 */
const DB_TO_UI_KEY_MAP: Record<string, string> = {
	sanctions_what_screened: "sanctionsScreenedItems",
	blockchain_vendor: "monitoringVendor",
	blockchain_process: "monitoringProcess",
	blockchain_thresholds: "monitoringThresholds",
	block_circumstances: "blockingCircumstances",
	prevention_controls: "blockingPreventionControls",
	ownership_chart_url: "ownershipStructureChartUrl",
	wallet_holder_info_collection: "walletHolderInfoCollected",
	wallet_holder_update_circumstances: "walletHolderInfoUpdateCircumstances",
	wallet_holder_update_frequency: "walletHolderInfoUpdateFrequency",
};

/**
 * Normalize a BFF fieldErrors key to UI camelCase.
 * If key contains underscore, assume snake_case and map; otherwise return as-is.
 */
export function dbKeyToUiKey(key: string): string {
	return key.includes("_")
		? (DB_TO_UI_KEY_MAP[key] ??
				key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase()))
		: key;
}
